import React from 'react'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import { SELECTED_FILTER_MAX_LINES } from 'store/constants/constants'
import { updateSelectedFilters } from 'store/actions/filters'
import SearchFilter from 'components/ui/SearchFilter'
import SelectedFilter from 'components/ui/SelectedFilter'
import FilterText from 'components/ui/FilterText'
import RemoveIcon from 'components/ui/RemoveIcon'
import SelectedFilterContainer from 'components/ui/SelectedFilterContainer'
import Dotdotdot from 'react-dotdotdot'
import CustomDropDownTreeSelect from 'components/ui/CustomDropdownTreeSelect'
import FilterTitle from 'components/ui/FilterTitle'

function MultiSelect({
  type,
  macroType,
  filterData,
  values,
  showFilter,
  customTop,
  selection,
  title
}) {
  const filters = useSelector(state => state.filters)
  const { currentSearchable } = filters

  const filterObject = macroType
    ? filters.selectedFilters[currentSearchable][macroType][type]
    : filters.selectedFilters[currentSearchable][type]

  return (
    <PureMultiSelect
      type={type}
      macroType={macroType}
      filterData={filterData}
      values={values}
      showFilter={showFilter}
      customTop={customTop}
      filterObject={filterObject}
      selection={selection}
      title={title}
    />
  )
}

const PureMultiSelect = React.memo(function PureMultiSelect({
  type,
  macroType,
  filterData,
  values,
  showFilter,
  customTop,
  filterObject,
  selection,
  title
}) {
  const dispatch = useDispatch()
  const selectedValues = filterObject[values[0]].map(option => ({
    label: option,
    id: option
  }))
  const options = filterData.map(option => {
    const parentOption = {
      label: `${option.substr(0, 1).toUpperCase()}${option.substr(1)}`,
      value: option,
      expanded: true
    }
    const selectedOption = selectedValues.filter(s => s.id === option)
    if (selectedOption.length > 0) {
      parentOption.checked = true
    }
    return parentOption
  })

  const onChange = (currentNode, selectedNodes) => {
    const filterValue = selectedNodes.map(node => node.value)
    const data = {
      filterType: type,
      filterMacroType: macroType,
      filterId: values[0],
      filterValue
    }
    dispatch(updateSelectedFilters(data))
  }

  function handleFilterRemove(filterId, filterValue, valueIsArray = false) {
    let resultingValue = null
    if (valueIsArray) {
      resultingValue = filterObject[filterId].filter(
        s => s.id !== filterValue.id || s !== filterValue
      )
    }
    const data = {
      filterType: type,
      filterMacroType: macroType,
      filterId,
      filterValue: resultingValue
    }
    dispatch(updateSelectedFilters(data))
  }

  function renderSelectedValues() {
    return values.map(value => {
      if (filterObject[value]) {
        const filterValue = filterObject[value]
        if (filterValue instanceof Array) {
          return filterValue.map(v => (
            <SelectedFilterContainer className="selected-filters" key={v.id ? v.id : v}>
              <SelectedFilter isCapitalized={value !== 'macro_regional_strategies'}>
                <FilterText title={v.label ? v.label : v}>
                  <Dotdotdot clamp={SELECTED_FILTER_MAX_LINES}>{v.label ? v.label : v}</Dotdotdot>
                </FilterText>
                <RemoveIcon
                  className="far fa-times"
                  onClick={() => handleFilterRemove(value, v, true)}
                />
              </SelectedFilter>
            </SelectedFilterContainer>
          ))
        }
      }
      return false
    })
  }

  return (
    <>
      {!selection ? (
        <SearchFilter open={showFilter} customTop={customTop}>
          <div className="sticky-container">
            {title ? <FilterTitle>{title}</FilterTitle> : null}
            <CustomDropDownTreeSelect data={options} onChange={onChange} />
          </div>
        </SearchFilter>
      ) : (
        <div className="selected-fl">{renderSelectedValues()}</div>
      )}
    </>
  )
})

MultiSelect.propTypes = {
  type: PropTypes.string,
  macroType: PropTypes.string,
  filterData: PropTypes.array,
  values: PropTypes.array,
  title: PropTypes.string,
  showFilter: PropTypes.bool,
  customTop: PropTypes.string,
  selection: PropTypes.bool
}

PureMultiSelect.propTypes = {
  type: PropTypes.string,
  macroType: PropTypes.string,
  filterData: PropTypes.array,
  values: PropTypes.array,
  title: PropTypes.string,
  showFilter: PropTypes.bool,
  customTop: PropTypes.string,
  filterObject: PropTypes.object,
  selection: PropTypes.bool
}

export default MultiSelect
